import { TabLines } from '@toggle/design-system';
import { ThemeSchemaResponse } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCondensedHeader } from '~/hooks/use-condensed-header/useCondensedHeader';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/app-paths';

import { ThemeHeader } from '../components/theme-header/ThemeHeader';
import * as S from './ThemeOverviewHeader.styles';

export type ThemeOverviewHeaderProps = {
  theme: ThemeSchemaResponse;
};

export const THEME_OVERVIEW_CONTENT_ID = 'theme-overview-content';
export const THEME_TAB_COMPANIES = 'companies';

export const ThemeOverviewHeader = ({ theme }: ThemeOverviewHeaderProps) => {
  const { t } = useTranslation('widget');
  const { params, setQueryParams } = useQueryParams();
  const activeTab = params[queryKeys.activeSubTab] || 'overview';
  const isScrolledEnough = useCondensedHeader({
    getRootElement: () => document.getElementById(THEME_OVERVIEW_CONTENT_ID),
    deps: [activeTab],
  });
  const isCondensed = activeTab !== 'overview' || isScrolledEnough;

  if (!theme) {
    throw new Error('ThemeOverviewHeader: No theme found');
  }

  const tabs = [
    {
      tabKey: 'overview',
      tabLabel: t('widget:themeOverview.overview'),
    },
    {
      tabKey: THEME_TAB_COMPANIES,
      tabLabel: t('widget:themeOverview.companies'),
    },
    {
      tabKey: 'performance',
      tabLabel: t('widget:themeOverview.performance'),
    },
    {
      tabKey: 'earnings',
      tabLabel: t('widget:themeOverview.earnings'),
    },
  ];

  return (
    <S.ThemeHeaderRoot data-testid="theme-overview-header">
      <S.InnerContainer $isCondensed={isCondensed}>
        <ThemeHeader
          themeName={theme.name}
          logo_url={theme.logo_url}
          isCondensed={isCondensed}
        />
        <TabLines
          tabs={tabs}
          activeTab={activeTab}
          onChange={idx =>
            setQueryParams({ [queryKeys.activeSubTab]: tabs[idx].tabKey })
          }
        />
      </S.InnerContainer>
    </S.ThemeHeaderRoot>
  );
};
